<template>
  <div class="alive-page">
    <header class="alive-header">
      <div>
        <h1>Мониторинг доступности</h1>
        <p>Проверка подключений к серверам записи</p>
      </div>
      <div class="alive-actions">
        <span class="alive-updated">{{ updatedLabel }}</span>
        <CButton color="primary" :disabled="loading" @click="loadStatus">
          {{ loading ? 'Обновление…' : 'Обновить' }}
        </CButton>
      </div>
    </header>

    <section class="alive-summary">
      <button class="summary-card" :class="{ active: filter === 'all' }" @click="filter = 'all'">
        <strong>{{ divisions.length }}</strong><span>Всего площадок</span>
      </button>
      <button class="summary-card summary-card_ok" :class="{ active: filter === 'online' }" @click="filter = 'online'">
        <strong>{{ onlineCount }}</strong><span>Полностью доступны</span>
      </button>
      <button class="summary-card summary-card_warning" :class="{ active: filter === 'partial' }" @click="filter = 'partial'">
        <strong>{{ partialCount }}</strong><span>Частично доступны</span>
      </button>
      <button class="summary-card summary-card_error" :class="{ active: filter === 'offline' }" @click="filter = 'offline'">
        <strong>{{ offlineCount }}</strong><span>Недоступны</span>
      </button>
    </section>

    <section class="alive-toolbar">
      <CFormInput v-model.trim="search" type="search" placeholder="Поиск по площадке, адресу, версии или серверу" />
      <CFormSelect v-model="sortBy" aria-label="Сортировка">
        <option value="health">Сначала проблемы</option>
        <option value="name">По названию</option>
        <option value="checked">По времени проверки</option>
        <option value="response">По времени ответа</option>
      </CFormSelect>
    </section>

    <div v-if="loading && !divisions.length" class="alive-empty">Загружаем данные мониторинга…</div>
    <div v-else-if="error" class="alive-empty alive-empty_error">{{ error }}</div>
    <div v-else-if="!filteredDivisions.length" class="alive-empty">Нет площадок, соответствующих фильтру.</div>

    <section v-else class="alive-grid">
      <article
        v-for="division in filteredDivisions"
        :key="division.divisionId"
        class="alive-card"
        :class="[`alive-card_${health(division)}`, { 'alive-card_pinging': isPinging(division) }]"
      >
        <div class="alive-card__head">
          <div>
            <h2>{{ division.divisionName || division.divisionId }}</h2>
            <span class="alive-version">v{{ ping(division)?.app?.version || '—' }}</span>
          </div>
          <div class="alive-card__actions">
            <button
              class="alive-ping"
              type="button"
              :disabled="isPinging(division)"
              title="Принудительная проверка"
              aria-label="Принудительная проверка"
              @click="forcePing(division)"
            >
              <i class="mdi" :class="isPinging(division) ? 'mdi-loading mdi-spin' : 'mdi-access-point'"></i>
            </button>
            <span class="alive-state" :class="`alive-state_${health(division)}`">{{ healthLabel(division) }}</span>
          </div>
        </div>

        <div class="protocols">
          <component
            :is="division[protocol.url] ? 'a' : 'span'"
            v-for="protocol in protocols"
            :key="protocol.key"
            class="protocol"
            :class="protocolAvailable(division, protocol) ? 'protocol_ok' : 'protocol_error'"
            :href="division[protocol.url] || undefined"
            :target="division[protocol.url] ? '_blank' : undefined"
            :rel="division[protocol.url] ? 'noopener' : undefined"
            :title="protocolBadgeTitle(division, protocol)"
          >
            <span class="protocol-dot"></span>{{ protocol.label }}
          </component>
        </div>

        <template v-if="ping(division)">
          <div class="metric-pairs">
            <div><span>Ответ</span><strong>{{ formatMs(ping(division).response_time_ms) }}</strong></div>
            <div><span>Камеры</span><strong>{{ recorderLabel(ping(division).recorders) }}</strong></div>
            <div><span>CPU</span><strong>{{ formatPercent(cpuPercent(ping(division))) }}</strong></div>
            <div><span>Память</span><strong>{{ formatPercent(memoryPercent(ping(division))) }}</strong></div>
          </div>
          <div class="health-notes">
            <span v-if="recorderIssues(ping(division).recorders)" class="health-note health-note_error">
              <i class="mdi mdi-video-off-outline"></i>{{ recorderIssues(ping(division).recorders) }} проблем с камерами
            </span>
            <span v-else class="health-note"><i class="mdi mdi-video-check-outline"></i>Запись в норме</span>
            <button class="health-note health-note_button" :class="notificationFailures(ping(division)) ? 'health-note_error' : ''" :title="latestNotification(ping(division))?.text || ''" @click="openNotifications(division)">
              <i class="mdi mdi-bell-outline"></i>{{ notificationLabel(ping(division)) }}
            </button>
            <span v-if="highDiskCount(ping(division))" class="health-note health-note_warning">
              <i class="mdi mdi-harddisk"></i>{{ highDiskCount(ping(division)) }} диск{{ highDiskCount(ping(division)) > 1 ? 'а' : '' }} заполнен
            </span>
          </div>
          <p v-if="latestNotification(ping(division))" class="latest-notification" :class="{ latest_notification_error: !latestNotification(ping(division)).success }">
            {{ latestNotification(ping(division)).text }}
          </p>
          <div class="alive-host">{{ ping(division).system?.hostname || 'Неизвестный сервер' }} · {{ ping(division).system?.os || '—' }}</div>
        </template>
        <p v-else class="alive-no-ping">Сервер не вернул данные ping.</p>

        <button class="details-toggle" @click="toggleDetails(division.divisionId)">
          {{ expanded.has(division.divisionId) ? 'Скрыть детали' : 'Показать детали' }}
        </button>
        <div v-if="expanded.has(division.divisionId)" class="alive-details">
          <div class="detail-row"><span>Проверено</span><span>{{ formatDate(division.checkedAtUtc) }}</span></div>
          <p v-if="division.failureReason" class="probe-failure probe-failure_summary">{{ division.failureReason }}</p>
          <div
            v-for="row in probeRows(division)"
            :key="row.protocol.key"
            class="probe-block"
            :class="probeBlockClass(division, row.protocol)"
          >
            <div class="probe-block__head">
              <strong>{{ row.protocol.label }}</strong>
              <span>{{ probeStatusLabel(division, row.protocol) }}</span>
            </div>
            <div v-if="row.probe?.elapsedMs != null" class="detail-row">
              <span>Время</span><span>{{ formatMs(row.probe.elapsedMs) }}</span>
            </div>
            <div v-if="row.url" class="detail-row">
              <span>URL</span><span class="probe-break">{{ row.url }}</span>
            </div>
            <div v-if="probeHttpStatus(row.probe)" class="detail-row">
              <span>HTTP</span><span>{{ probeHttpStatus(row.probe) }}</span>
            </div>
            <div v-if="row.probe?.httpRequestError" class="detail-row">
              <span>Запрос</span><span class="probe-break">{{ row.probe.httpRequestError }}</span>
            </div>
            <div v-if="row.probe?.socketError" class="detail-row">
              <span>Сокет</span><span class="probe-break">{{ row.probe.socketError }}</span>
            </div>
            <div v-if="row.probe?.exceptionType" class="detail-row">
              <span>Исключение</span><span class="probe-break">{{ row.probe.exceptionType }}</span>
            </div>
            <p v-if="row.probe?.failureReason" class="probe-failure">
              {{ row.probe.failureReason }}
            </p>
          </div>
          <template v-if="ping(division)">
            <div class="detail-row"><span>ОС</span><span>{{ ping(division).system?.os }} {{ ping(division).system?.os_version }}</span></div>
            <div class="detail-row"><span>Аптайм процесса</span><span>{{ formatUptime(ping(division).process?.run_time_seconds) }}</span></div>
            <div class="detail-row"><span>Заданий конкатенации</span><span>{{ ping(division).concat_jobs?.total ?? '—' }}</span></div>
            <div class="detail-row"><span>Уведомлений</span><span>{{ ping(division).notifications?.total_stored ?? '—' }}</span></div>
            <div v-for="notification in recentNotifications(ping(division))" :key="notification.timestamp + notification.text" class="notification-row" :class="{ 'notification-row_error': !notification.success }">
              <span>{{ formatDate(notification.timestamp) }} · {{ notification.notify_type }}</span>
              <span>{{ notification.text }}</span>
            </div>
            <div v-for="disk in ping(division).disks || []" :key="disk.mount_point" class="disk-row">
              <div><span>{{ disk.mount_point }} {{ disk.name }}</span><span>{{ formatBytes(disk.used_bytes) }} / {{ formatBytes(disk.total_bytes) }}</span></div>
              <div class="disk-bar"><i :class="progressClass(disk.usage_percent)" :style="{ width: `${Math.min(disk.usage_percent || 0, 100)}%` }"></i></div>
            </div>
          </template>
          <template v-else>
            <div class="detail-row"><span>ID площадки</span><span class="detail-id">{{ division.divisionId }}</span></div>
          </template>
        </div>
      </article>
    </section>

    <Teleport to="body">
      <div v-if="notificationDivision" class="notifications-overlay" @click.self="closeNotifications">
        <section class="notifications-modal" role="dialog" aria-modal="true" aria-label="Уведомления сервера">
          <header class="notifications-modal__header">
            <div>
              <h2>Уведомления</h2>
              <p>{{ notificationDivision.divisionName }} · {{ notificationList.length }}</p>
            </div>
            <button class="notifications-modal__close" aria-label="Закрыть" @click="closeNotifications">×</button>
          </header>
          <div v-if="notificationList.length" class="notifications-list">
            <article v-for="notification in notificationList" :key="notification.timestamp + notification.text" class="notification-item" :class="{ 'notification-item_error': !notification.success }">
              <div class="notification-item__meta">
                <span>{{ notification.notify_type || 'Уведомление' }}</span>
                <time>{{ formatDate(notification.timestamp) }}</time>
              </div>
              <p>{{ notification.text || 'Нет текста уведомления' }}</p>
              <p v-if="notification.error_message" class="notification-item__error">{{ notification.error_message }}</p>
            </article>
          </div>
          <p v-else class="notifications-empty">Уведомлений нет.</p>
        </section>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { CButton, CFormInput, CFormSelect } from '@coreui/bootstrap-vue'
import { ApiSys } from '@/services/api'
import {
  PING_PROTOCOLS,
  getProbe,
  notifyPingResult,
  probeHttpStatus,
  probeStatusLabel,
  protocolBadgeTitle,
  unwrapPingStatus
} from '@/services/division-ping'
import { useDefaultStore } from '@/store'

export default {
  name: 'AliveStatusPage',
  components: { CButton, CFormInput, CFormSelect },
  setup() { return { store: useDefaultStore() } },
  data() {
    return {
      divisions: [], loading: false, error: '', search: '', filter: 'all', sortBy: 'health',
      updatedAt: null, refreshTimer: null, expanded: new Set(), pinging: new Set(), notificationDivision: null,
      protocols: PING_PROTOCOLS
    }
  },
  computed: {
    onlineCount() { return this.divisions.filter(item => this.health(item) === 'online').length },
    partialCount() { return this.divisions.filter(item => this.health(item) === 'partial').length },
    offlineCount() { return this.divisions.filter(item => this.health(item) === 'offline').length },
    updatedLabel() { return this.updatedAt ? `Обновлено ${this.formatDate(this.updatedAt)}` : '' },
    notificationList() { return [...(this.ping(this.notificationDivision)?.notifications?.notifications || [])].reverse() },
    filteredDivisions() {
      const needle = this.search.toLowerCase()
      return this.divisions.filter(item => {
        const matchesFilter = this.filter === 'all' || this.health(item) === this.filter
        const matchesSearch = !needle || [item.divisionName, item.divisionId, item.addressPingUrl, item.urlPingUrl, item.httpsPingUrl, this.ping(item)?.system?.hostname, this.ping(item)?.app?.version].filter(Boolean).join(' ').toLowerCase().includes(needle)
        return matchesFilter && matchesSearch
      }).sort((a, b) => this.compare(a, b))
    }
  },
  mounted() {
    this.store.setPageTitle('Мониторинг доступности')
    this.loadStatus()
    this.refreshTimer = setInterval(() => this.loadStatus(true), 30000)
  },
  beforeUnmount() { clearInterval(this.refreshTimer) },
  methods: {
    errorMessage(response, fallback) {
      return response?.error?.response?.data?.message ||
        response?.error?.response?.data?.error?.message ||
        response?.error?.message ||
        fallback
    },
    async loadStatus(silent = false) {
      if (!silent) this.loading = true
      if (!silent) this.error = ''
      const result = await ApiSys.getAliveStatus()
      if (!silent) this.loading = false
      if (result?.error) {
        if (!silent) this.error = 'Не удалось загрузить статус доступности.'
        return
      }
      const data = result?.data ?? result
      if (!data || Array.isArray(data) || typeof data !== 'object') {
        if (!silent) this.error = 'Сервер вернул данные в неизвестном формате.'
        return
      }
      this.divisions = Object.values(data)
      this.updatedAt = new Date()
    },
    isPinging(item) {
      return this.pinging.has(item?.divisionId)
    },
    async forcePing(division) {
      const id = division?.divisionId
      if (!id || this.pinging.has(id)) return

      const pinging = new Set(this.pinging)
      pinging.add(id)
      this.pinging = pinging

      const result = await ApiSys.pingAdminDivision(id)

      const next = new Set(this.pinging)
      next.delete(id)
      this.pinging = next

      if (result?.error) {
        this.$toast.error(this.errorMessage(result, 'Не удалось проверить площадку'))
        return
      }

      const status = unwrapPingStatus(result)
      this.applyPingStatus(id, status)
      notifyPingResult(this.$toast, status, division.divisionName || id)
    },
    applyPingStatus(id, status) {
      if (!status || typeof status !== 'object') {
        return
      }

      const divisionId = status.divisionId || id
      const next = [...this.divisions]
      const index = next.findIndex(item => item.divisionId === divisionId || item.divisionId === id)
      const merged = { ...(index >= 0 ? next[index] : {}), ...status, divisionId }
      if (index >= 0) {
        next[index] = merged
      } else {
        next.push(merged)
      }
      this.divisions = next
      this.updatedAt = new Date()
    },
    getProbe,
    protocolBadgeTitle,
    probeStatusLabel,
    probeHttpStatus,
    protocolAvailable(division, protocol) {
      const probe = this.getProbe(division, protocol)
      if (typeof probe?.available === 'boolean') return probe.available
      return Boolean(division?.[protocol.available])
    },
    probeRows(division) {
      return this.protocols.map(protocol => {
        const probe = this.getProbe(division, protocol)
        return {
          protocol,
          probe,
          url: probe?.url || division?.[protocol.url] || null
        }
      })
    },
    probeBlockClass(division, protocol) {
      const probe = this.getProbe(division, protocol)
      const available = probe ? probe.available : division?.[protocol.available]
      if (probe?.outcome === 'Skipped' || (available == null && !probe)) return 'probe-block_muted'
      return available ? 'probe-block_ok' : 'probe-block_error'
    },
    ping(item) { return item?.pingData || null },
    health(item) {
      const available = ['addressAvailable', 'urlAvailable', 'httpsAvailable'].filter(key => item?.[key]).length
      if (available === 3 && this.ping(item)?.status === 'ok') return 'online'
      if (available === 0) return 'offline'
      return 'partial'
    },
    healthLabel(item) { return { online: 'Доступен', partial: 'Частично доступен', offline: 'Недоступен' }[this.health(item)] },
    compare(a, b) {
      if (this.sortBy === 'name') return (a.divisionName || '').localeCompare(b.divisionName || '', 'ru')
      if (this.sortBy === 'checked') return new Date(b.checkedAtUtc || 0) - new Date(a.checkedAtUtc || 0)
      if (this.sortBy === 'response') return (this.ping(b)?.response_time_ms ?? Infinity) - (this.ping(a)?.response_time_ms ?? Infinity)
      const rank = { offline: 0, partial: 1, online: 2 }
      return rank[this.health(a)] - rank[this.health(b)] || (a.divisionName || '').localeCompare(b.divisionName || '', 'ru')
    },
    toggleDetails(id) { const next = new Set(this.expanded); next.has(id) ? next.delete(id) : next.add(id); this.expanded = next },
    openNotifications(division) { this.notificationDivision = division },
    closeNotifications() { this.notificationDivision = null },
    cpuPercent(data) { const value = data?.cpu_usage_percent; return value == null ? null : value / (data?.system?.cpu_count || 1) },
    memoryPercent(data) { return data?.memory?.total_bytes ? data.memory.used_bytes / data.memory.total_bytes * 100 : null },
    recorderLabel(recorders) { return recorders ? `${recorders.running ?? 0} / ${recorders.total ?? 0}` : '—' },
    recorderIssues(recorders) { return (recorders?.stopped || 0) + (recorders?.hung_count || 0) },
    notificationFailures(data) { return (data?.notifications?.notifications || []).filter(item => !item.success).length },
    notificationLabel(data) { const total = data?.notifications?.total_stored || 0; const failed = this.notificationFailures(data); return failed ? `${total} уведомл. · ${failed} ошибка` : `${total} уведомлений` },
    latestNotification(data) { return data?.notifications?.notifications?.at(-1) || null },
    recentNotifications(data) { return [...(data?.notifications?.notifications || [])].slice(-3).reverse() },
    highDiskCount(data) { return (data?.disks || []).filter(disk => disk.usage_percent >= 95).length },
    formatPercent(value) { return value == null ? '—' : `${value.toFixed(1)}%` },
    formatMs(value) { return value == null ? '—' : `${Math.round(value)} мс` },
    formatBytes(value) { if (value == null) return '—'; const units = ['Б', 'КБ', 'МБ', 'ГБ', 'ТБ']; const index = Math.min(Math.floor(Math.log(value || 1) / Math.log(1024)), units.length - 1); return `${(value / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}` },
    formatDate(value) { if (!value) return '—'; return new Intl.DateTimeFormat('ru-RU', { dateStyle: 'short', timeStyle: 'medium' }).format(new Date(value)) },
    formatUptime(value) { if (value == null) return '—'; const days = Math.floor(value / 86400); const hours = Math.floor(value % 86400 / 3600); return `${days} д ${hours} ч` },
    progressClass(value) { return value >= 90 ? 'progress_danger' : value >= 75 ? 'progress_warning' : '' }
  }
}
</script>

<style scoped>
.alive-page { height: 100%; overflow-y: auto; overscroll-behavior: contain; box-sizing: border-box; padding: 1rem 1.5rem; color: #d9e1e8; scrollbar-width: thin; }
.alive-header, .alive-actions, .alive-toolbar, .alive-summary, .alive-card__head, .protocols, .metric-pairs, .detail-row, .disk-row > div { display: flex; }
.alive-header { justify-content: space-between; gap: 1rem; align-items: center; margin-bottom: 1.25rem; }
.alive-header h1 { margin: 0; font-size: 1.5rem; }.alive-header p { margin: .2rem 0 0; color: #83909d; font-size: .85rem; }.alive-actions { gap: .75rem; align-items: center; }.alive-updated { color: #83909d; font-size: .75rem; }
.alive-summary { gap: .55rem; margin-bottom: .75rem; flex-wrap: wrap; }.summary-card { min-width: 130px; flex: 1; border: 1px solid rgba(255,255,255,.1); border-radius: 7px; background: rgba(255,255,255,.035); color: #cbd5df; text-align: left; padding: .55rem .8rem; cursor: pointer; }.summary-card strong { display: block; font-size: 1.3rem; color: #fff; }.summary-card span { font-size: .7rem; }.summary-card.active { outline: 2px solid #438eea; }.summary-card_ok strong { color: #44c180; }.summary-card_warning strong { color: #edb44d; }.summary-card_error strong { color: #ed6666; }
.alive-toolbar { gap: .75rem; margin-bottom: 1rem; }.alive-toolbar > *:first-child { flex: 1; }.alive-toolbar > *:last-child { width: 190px; }.alive-empty { padding: 3rem; color: #92a0ad; text-align: center; }.alive-empty_error { color: #ef7878; }
.alive-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(255px, 1fr)); gap: .65rem; }.alive-card { background: rgba(255,255,255,.035); border: 1px solid rgba(255,255,255,.09); border-left: 4px solid #77828d; border-radius: 7px; padding: .7rem; }.alive-card_online { border-left-color: #41bb7b; }.alive-card_partial { border-left-color: #e5ae44; }.alive-card_offline { border-left-color: #db5f5f; }
.alive-card__head { justify-content: space-between; align-items: start; gap: .5rem; }.alive-card h2 { margin: 0; font-size: .92rem; word-break: break-word; }.alive-version, .alive-host { color: #8c9aa8; font-size: .68rem; }.alive-state { padding: .14rem .38rem; border-radius: 1rem; font-size: .62rem; white-space: nowrap; }.alive-state_online { color: #67dc9d; background: rgba(65,187,123,.14); }.alive-state_partial { color: #f2c563; background: rgba(229,174,68,.14); }.alive-state_offline { color: #f28282; background: rgba(219,95,95,.14); }
.alive-card__actions { display: flex; align-items: center; flex-shrink: 0; gap: .35rem; }.alive-card_pinging { opacity: .78; }.alive-ping { display: inline-grid; place-items: center; width: 24px; height: 24px; padding: 0; color: #8b97a3; background: transparent; border: 1px solid rgba(255,255,255,.12); border-radius: 6px; cursor: pointer; }.alive-ping:hover:not(:disabled) { color: #b8d9fa; background: rgba(56,154,240,.12); border-color: rgba(56,154,240,.24); }.alive-ping:disabled { opacity: .55; cursor: default; }
.protocols { gap: .3rem; margin: .55rem 0; }.protocol { color: #a9b5c1; text-decoration: none; border: 1px solid rgba(255,255,255,.1); border-radius: 4px; padding: .12rem .3rem; font-size: .62rem; }.protocol-dot { display: inline-block; width: 5px; height: 5px; border-radius: 50%; margin-right: .25rem; }.protocol_ok .protocol-dot { background: #42bd7d; }.protocol_error .protocol-dot { background: #df6161; }
.metric-pairs { display: grid; grid-template-columns: repeat(4, 1fr); gap: .3rem; padding: .5rem 0; border-top: 1px solid rgba(255,255,255,.07); }.metric-pairs div { display: flex; flex-direction: column; min-width: 0; }.metric-pairs span { color: #84919d; font-size: .62rem; }.metric-pairs strong { font-size: .72rem; white-space: nowrap; }.health-notes { display: flex; flex-wrap: wrap; gap: .3rem; padding: .45rem 0; border-top: 1px solid rgba(255,255,255,.07); }.health-note { color: #9ba9b5; font-size: .62rem; }.health-note i { margin-right: .16rem; }.health-note_button { border: 0; background: transparent; padding: 0; cursor: pointer; }.health-note_button:hover { color: #80b9fc; text-decoration: underline; }.health-note_error { color: #ef8484; }.health-note_warning { color: #edc15c; }.latest-notification { color: #97a5b0; font-size: .65rem; margin: 0 0 .4rem; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }.latest_notification_error { color: #e68181; }.alive-host { border-top: 1px solid rgba(255,255,255,.07); padding-top: .45rem; }.alive-no-ping { color: #d98989; font-size: .75rem; margin: .7rem 0; }
.details-toggle { padding: 0; border: 0; background: none; color: #73aff7; font-size: .68rem; cursor: pointer; margin-top: .55rem; }.alive-details { margin-top: .55rem; padding-top: .5rem; border-top: 1px solid rgba(255,255,255,.08); }.detail-row { justify-content: space-between; gap: 1rem; padding: .18rem 0; font-size: .68rem; }.detail-row span:first-child { color: #8c99a5; }.detail-row span:last-child { text-align: right; word-break: break-word; }.detail-id { font-family: monospace; }.probe-block { margin-top: .45rem; padding: .4rem .45rem; border: 1px solid rgba(255,255,255,.08); border-left: 3px solid #77828d; border-radius: 5px; }.probe-block_ok { border-left-color: #41bb7b; }.probe-block_error { border-left-color: #db5f5f; }.probe-block_muted { border-left-color: #6b7681; }.probe-block__head { display: flex; justify-content: space-between; gap: .5rem; align-items: baseline; font-size: .68rem; }.probe-block__head strong { color: #d5dee6; }.probe-block__head span { color: #8c99a5; text-align: right; }.probe-break, .probe-failure { overflow-wrap: anywhere; word-break: break-word; }.probe-failure { margin: .35rem 0 0; color: #ef8484; font-size: .65rem; white-space: pre-wrap; }.probe-failure_summary { margin: .35rem 0 .2rem; }.notification-row { border-left: 2px solid #4dbb80; margin-top: .45rem; padding-left: .4rem; display: flex; flex-direction: column; gap: .12rem; font-size: .65rem; }.notification-row_error { border-left-color: #dd6565; }.notification-row span:first-child { color: #8c99a5; }.disk-row { display: block; padding-top: .5rem; font-size: .7rem; }.disk-row > div:first-child { justify-content: space-between; gap: .4rem; }.disk-row span:first-child { color: #aebac5; }.disk-row span:last-child { color: #84919d; }.disk-bar { height: 5px; background: rgba(255,255,255,.1); border-radius: 4px; margin-top: .3rem; overflow: hidden; }.disk-bar i { background: #45bb80; height: 100%; display: block; }.disk-bar .progress_warning { background: #e3ae48; }.disk-bar .progress_danger { background: #dc6161; }
.notifications-overlay { position: fixed; z-index: 2000; inset: 0; display: flex; align-items: center; justify-content: center; padding: 1rem; background: rgba(0,0,0,.62); }.notifications-modal { width: min(760px, 100%); max-height: min(760px, calc(100vh - 2rem)); display: flex; flex-direction: column; border: 1px solid rgba(255,255,255,.15); border-radius: 9px; background: #202831; color: #dce5ec; box-shadow: 0 1rem 3rem rgba(0,0,0,.45); }.notifications-modal__header { display: flex; justify-content: space-between; align-items: flex-start; padding: 1rem 1.1rem; border-bottom: 1px solid rgba(255,255,255,.09); }.notifications-modal h2 { margin: 0; font-size: 1.05rem; }.notifications-modal__header p { margin: .18rem 0 0; color: #8d9aa6; font-size: .75rem; }.notifications-modal__close { border: 0; color: #aeb9c3; background: transparent; font-size: 1.5rem; line-height: 1; cursor: pointer; }.notifications-list { overflow-y: auto; padding: .75rem 1rem; }.notification-item { border-left: 3px solid #4cbd81; background: rgba(255,255,255,.035); padding: .6rem .75rem; margin-bottom: .5rem; border-radius: 0 5px 5px 0; }.notification-item_error { border-left-color: #de6565; }.notification-item__meta { display: flex; justify-content: space-between; gap: 1rem; color: #9eabb6; font-size: .7rem; }.notification-item__meta span { color: #bfccd6; }.notification-item p { margin: .35rem 0 0; font-size: .78rem; white-space: pre-wrap; word-break: break-word; }.notification-item .notification-item__error { color: #ef8787; font-family: monospace; font-size: .67rem; }.notifications-empty { margin: 2rem; text-align: center; color: #8e9ca9; }
@media (max-width: 700px) { .alive-page { padding: 1rem; }.alive-header { align-items: flex-start; flex-direction: column; }.alive-actions { width: 100%; justify-content: space-between; }.alive-toolbar { flex-direction: column; }.alive-toolbar > *:last-child { width: 100%; }.summary-card { min-width: calc(50% - .4rem); }.alive-grid { grid-template-columns: 1fr; } }
</style>
